import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Browser-native text-to-speech.
 * Uses window.speechSynthesis — works offline, no data sent anywhere.
 */
export function useTTS() {
  const [supported, setSupported] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const voiceRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    setSupported(true);

    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;
      // Prefer a calm English voice. iOS/macOS "Samantha", Chrome "Google UK English Female".
      const preferred =
        voices.find((v) => /Samantha/i.test(v.name)) ||
        voices.find((v) => /Google UK English Female/i.test(v.name)) ||
        voices.find((v) => /en-GB/i.test(v.lang)) ||
        voices.find((v) => /en-US/i.test(v.lang)) ||
        voices.find((v) => /^en/i.test(v.lang)) ||
        voices[0];
      voiceRef.current = preferred || null;
    };

    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;
    return () => {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {
        // ignore
      }
    };
  }, []);

  const speak = useCallback(
    (text) => {
      if (!supported || !text) return;
      const synth = window.speechSynthesis;
      synth.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      if (voiceRef.current) utt.voice = voiceRef.current;
      utt.rate = 0.98;
      utt.pitch = 1.0;
      utt.onstart = () => setSpeaking(true);
      utt.onend = () => setSpeaking(false);
      utt.onerror = () => setSpeaking(false);
      synth.speak(utt);
    },
    [supported]
  );

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  return { supported, speaking, speak, stop };
}

/**
 * Browser-native speech-to-text dictation.
 * Uses webkitSpeechRecognition / SpeechRecognition. Each transcribed phrase is appended via onAppend.
 */
export function useSTT({ onAppend }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const onAppendRef = useRef(onAppend);

  useEffect(() => {
    onAppendRef.current = onAppend;
  }, [onAppend]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-GB";

    rec.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = (result[0].transcript || "").trim();
          if (text && onAppendRef.current) onAppendRef.current(text);
        }
      }
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);

    recRef.current = rec;
    return () => {
      try {
        rec.stop();
      } catch (_) {
        // ignore
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!supported || !recRef.current) return;
    try {
      recRef.current.start();
      setListening(true);
    } catch (_) {
      // already started, ignore
    }
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported || !recRef.current) return;
    try {
      recRef.current.stop();
    } catch (_) {
      // ignore
    }
    setListening(false);
  }, [supported]);

  const toggle = useCallback(() => {
    listening ? stop() : start();
  }, [listening, start, stop]);

  return { supported, listening, start, stop, toggle };
}
